import type { AxiosHeaders } from 'axios';
import { createQueryParams } from '@/utils/query-params';
import { api } from './index';

const PATIENTS_PATH = '/api/internal/patients';

// Mirrors ClinicalAccessEvent::CHANNEL_GROUPS.
export type ClinicalAccessChannelGroup =
  | 'web'
  | 'caregiver_app'
  | 'family_app'
  | 'kuido'
  | 'email'
  | 'export';

// One Acceso of the patient's access log: identical views within 10 minutes are
// merged into one entry, with their count and the time of the last one.
export type ClinicalAccessEvent = {
  id: number;
  firstAt: string;
  lastAt: string;
  humanFirstAt: string;
  // Only the time when the last view was on the first one's day.
  humanLastAt: string;
  viewsCount: number;
  person: string;
  channelGroup: ClinicalAccessChannelGroup;
  humanChannel: string;
  action: 'view' | 'print' | 'export' | 'email';
  humanAction: string;
  section: string;
  humanSection: string;
};

// value: "Caregiver:12", the person filter's value.
export type ClinicalAccessPerson = { value: string; title: string };

// Both dates are required (YYYY-MM-DD, at most 366 days apart, both included).
export type ClinicalAccessFilters = {
  from: string;
  to: string;
  person?: string | null;
  channel?: ClinicalAccessChannelGroup | null;
};

export type ClinicalAccessPage = {
  events: ClinicalAccessEvent[];
  totalCount: number;
  // Everyone in the date range, whatever the other filters.
  people: ClinicalAccessPerson[];
};

export const clinicalAccessEventsApi = {
  index(
    patientId: number,
    filters: ClinicalAccessFilters,
    page: number,
    perPage: number,
  ): Promise<ClinicalAccessPage> {
    const query = createQueryParams({ ...filters, page, perPage });

    return api({
      method: 'get',
      url: `${PATIENTS_PATH}/${patientId}/clinical_access_events?${query}`,
    }).then(response => ({
      events: response.data.clinicalAccessEvents,
      totalCount: response.data.meta.totalCount,
      people: response.data.meta.people,
    }));
  },
  download(
    patientId: number,
    filters: ClinicalAccessFilters,
  ): Promise<{ headers: AxiosHeaders; data: Blob }> {
    return api({
      method: 'get',
      url: `${PATIENTS_PATH}/${patientId}/clinical_access_events.csv?${createQueryParams(filters)}`,
      responseType: 'blob',
    });
  },
};
